<template>
    <div>
        <div style="margin-bottom: 10px">
            <div class="control has-icons-left">
                <input
                    v-model="contractsFilterText"
                    type="text"
                    class="input"
                    placeholder="Filter available contracts">
                <span class="icon is-small is-left">
                    <i class="mdi mdi-magnify" />
                </span>
            </div>
        </div>
        <set-selector
            v-if="true"
            :key="contractSetSelectorKey"
            :identity="(x) => x.identity"
            :sort-by="['element.name']"
            :initial-elements="availableContracts"
            :initial-selection="selectedContracts"
            :filter="contractFilter"
            available-elements-title="Available contracts"
            selected-elements-title="Selected contracts"
            @save="contractRelationsSave">
            <template #left-item="props">
                <div
                    class="notification is-light"
                    style="margin-bottom: 2px">
                    <span><b>{{ props.element.name }}</b>
                        {{ props.element.description }}</span>
                </div>
            </template>
            <template #right-item="props">
                <div
                    class="notification is-light"
                    style="margin-bottom: 2px">
                    <span><b>{{ props.element.name }}</b>
                        {{ props.element.description }}</span>
                </div>
            </template>
        </set-selector>
    </div>
</template>

<script>
    import _ from 'lodash';
    import SetSelector from '@/shared/components/SetSelector.vue';
    import { showMixin } from '@/shared/mixins/showMixin';
    import {
        getCodeSets,
        createChangeDocAndCommitCodesets,
        sqlQuery
    } from '@/shared/helpers/api';

    export default {
        components: {
            SetSelector
        },
        mixins: [showMixin],
        props: {
            projectMaster: {
                type: Object,
                default: null
            }
        },
        emits: [
            'refresh'
        ],
        data() {
            return {
                allContracts: {},
                contractProjectMasterRelations: [],
                contractsFilterText: '',
                contractSetSelectorKey: 1,
                contractFilter: (x) => x,
                codeSetName: '',
                contractProjectMasterCodeSetName: ''
            };
        },
        computed: {
            contractsViewModel() {
                return this.allContracts;
            },
            // Guards against the same contract appearing more than once in the two lists,
            // which SetSelector would render as duplicate entries
            uniqueContracts() {
                return _.uniqBy(this.allContracts?.data ?? [], (x) => x.identity);
            },
            availableContracts() {
                return this.uniqueContracts.filter(
                    (x) => !this.isRelatedToProjectMaster(x)
                );
            },
            selectedContracts() {
                return this.uniqueContracts.filter(
                    (x) => this.isRelatedToProjectMaster(x)
                );
            }
        },
        watch: {
            contractsFilterText(value) {
                if (value === '') this.contractFilter = (x) => x;

                const parts = value.toLowerCase().split(' ');
                this.contractFilter = function(x) {
                    const tmp = (x.name + x.description).toLowerCase();
                    return _.every(parts, function(p) {
                        return tmp.indexOf(p) > -1;
                    });
                };
            },
            contractsViewModel(value) {
                if (value) {
                    this.contractSetSelectorKey += 1;
                }
            }
        },
        async created() {
            const [codeSets, contractProjectMasterCodeSets] = await Promise.all([
                getCodeSets(this, 'Contract'),
                getCodeSets(this, 'Contract_ProjectMaster')
            ]);
            this.codeSetName = codeSets[0].name;
            this.contractProjectMasterCodeSetName = contractProjectMasterCodeSets[0].name;
            await this.loadContracts();
        },
        methods: {
            // A contract belongs to this project master when the direct CodeRef points here,
            // or when there is a valid Contract_ProjectMaster relation. A contract directly
            // assigned to a different project master can still be related to this one.
            isRelatedToProjectMaster(contract) {
                return contract.project === this.projectMaster.Identity
                    || this.hasValidRelation(contract.identity);
            },
            hasValidRelation(contractIdentity) {
                return this.contractProjectMasterRelations.some(
                    (r) => r.contract === contractIdentity && r.isValid
                );
            },
            hasRelation(contractIdentity) {
                return this.contractProjectMasterRelations.some(
                    (r) => r.contract === contractIdentity
                );
            },
            async loadContracts() {
                // Relations must be loaded first, they are used when computing the two lists
                this.contractProjectMasterRelations = await this.fetchContractProjectMasterRelations();
                this.allContracts = await this.fetchContracts();
            },
            async fetchContracts() {
                const query
                    = `SELECT
                        [Contract].[Name] as [name],
                        [Contract].[Identity] as [identity],
                        [Contract].[Description] as [description],
                        [Contract].[IsValid] as [isValid],
                        [Contract].[ProjectMaster] as [project]
                    FROM [Contract]
                    WHERE [Contract].[IsValid] = 1
                    ORDER BY [Contract].[Name]`;
                return await sqlQuery(this, { query });
            },
            async fetchContractProjectMasterRelations() {
                const query
                    = `SELECT
                        [Contract_ProjectMaster].[Contract] as [contract],
                        [Contract_ProjectMaster].[IsValid] as [isValid]
                    FROM [Contract_ProjectMaster]
                    WHERE [Contract_ProjectMaster].[ProjectMaster] = @projectIdentity`;
                return (await sqlQuery(this, {
                    query,
                    arguments: { projectIdentity: this.projectMaster.Identity }
                })).data;
            },
            async contractRelationsSave(state) {
                try {
                    const projectMasterIdentity = this.projectMaster.Identity;

                    // Keep the direct CodeRef in sync with the relation library:
                    // set it on add only when the contract has no project master yet, so a
                    // contract already assigned to another project master is not stolen from it.
                    // On remove it is cleared when it points at this project master, otherwise
                    // the contract would stay related through it.
                    const contractChanges = _.concat(
                        state.added
                            .filter(contract => !contract.project)
                            .map(contract => ({
                                ProjectMaster: projectMasterIdentity,
                                Name: contract.name
                            })),
                        state.removed
                            .filter(contract => contract.project === projectMasterIdentity)
                            .map(contract => ({
                                ProjectMaster: '{null}',
                                Name: contract.name
                            }))
                    );

                    // The Contract_ProjectMaster relation library.
                    // Removed contracts without an existing relation are left untouched.
                    const relationChanges = _.concat(
                        state.added.map(contract => ({
                            Contract: contract.identity,
                            ProjectMaster: projectMasterIdentity,
                            IsValid: true
                        })),
                        state.removed
                            .filter(contract => this.hasRelation(contract.identity))
                            .map(contract => ({
                                Contract: contract.identity,
                                ProjectMaster: projectMasterIdentity,
                                IsValid: false
                            }))
                    );

                    const documents = [
                        { codeSetName: this.codeSetName, codes: contractChanges },
                        { codeSetName: this.contractProjectMasterCodeSetName, codes: relationChanges }
                    ].filter(doc => doc.codes.length > 0);

                    if (documents.length === 0) return;

                    const res = await createChangeDocAndCommitCodesets(this, documents);
                    this.showInfo(res.data.message);
                    await this.loadContracts();
                    this.$emit('refresh');
                } catch (err) {
                    this.showError(err);
                }
            }
        }
    };
</script>
